import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PlacesAutoComplete } from '@components/google-location/google-location.component';
import { TravelHistoryData } from '@models/medical-history.model';
import { AlertService } from '@services/alert.service';
import { DisplayService } from '@services/display.service';
import { MedicalHistoryService } from '@services/medical-history.service';
import { BehaviorSubject, take } from 'rxjs';

@Component({
  selector: 'gc-travel-history',
  templateUrl: './travel-history.component.html',
  styleUrls: ['./travel-history.component.scss'],
  standalone: false,
})
export class TravelHistoryComponent implements OnInit, OnChanges {
  @Input() userId: number;
  @Input() travelHistory: TravelHistoryData[] = [];
  @Input() requestUuid: string | null = null;
  @Output() submittedStep = new EventEmitter<{ step: string; values: any }>();

  travelHistoryForm: FormGroup;
  historyListForm: FormGroup;
  panelOpenState = false;
  isMobile$: BehaviorSubject<boolean>;

  constructor(
    private readonly fb: FormBuilder,
    private readonly medicalHistoryService: MedicalHistoryService,
    private readonly alertService: AlertService,
    private readonly displayService: DisplayService,
  ) {}

  ngOnInit() {
    this.buildForms();
    this.isMobile$ = this.displayService.isMobile$;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['travelHistory'] && !changes['travelHistory'].firstChange) {
      this.buildForms();
    }
  }

  buildForms() {
    this.travelHistoryForm = this.initRow({
      to: null,
      from: null,
      location: '',
      additional_information: '',
    });

    this.travelHistory = this.travelHistory ? this.travelHistory : [];

    this.historyListForm = this.fb.group({
      rowList: this.fb.array(this.travelHistory.map((travel) => this.initRow(travel))),
    });
  }

  checkDateValid(control: AbstractControl): boolean {
    const parentControl = control.parent.controls;
    const controlName = Object.keys(parentControl).find((name) => parentControl[name] === control);
    if (control.touched && control.value === '') {
      this.travelHistoryForm.get(controlName).setErrors({ invalid: true });
      return true;
    }
    return false;
  }

  initRow(travel: TravelHistoryData): FormGroup {
    return this.fb.group({
      to: [travel.to || '', Validators.required],
      from: [travel.from || '', Validators.required],
      location: [travel.location || ''],
      additional_information: [travel.additional_information || ''],
    });
  }

  get rowListControl(): FormArray {
    return this.historyListForm.get('rowList') as FormArray;
  }

  populateAddress(place: PlacesAutoComplete, index?: number) {
    if (index === undefined) {
      this.travelHistoryForm.get('location').patchValue(place.formattedAddress);
    } else {
      this.rowListControl.at(index).get('location').patchValue(place.formattedAddress);
    }
  }

  addTravel() {
    if (this.travelHistoryForm.valid) {
      this.rowListControl.push(this.initRow(this.travelHistoryForm.value));
      this.travelHistoryForm.reset();
    }
  }

  deleteTravel(index: number) {
    this.rowListControl.removeAt(index);
  }

  saveToRequest() {
    this.medicalHistoryService
      .saveRequestMedicalHistory$(this.requestUuid, 'travel_history', this.rowListControl.value)
      .pipe(take(1))
      .subscribe((request) => {
        if (request) {
          this.submittedStep.emit({ step: 'travelHistory', values: this.rowListControl.value });
          this.alertService.snackbar('travel_history_saved');
        }
      });
  }

  saveToProfile() {
    this.medicalHistoryService
      .saveMedicalHistory$(this.userId, 'travel_history', { user_id: this.userId, travel_history: this.rowListControl.value })
      .subscribe(() => this.alertService.snackbar('travel_history_saved'));
  }

  submit() {
    if (this.requestUuid) {
      this.saveToRequest();
    } else {
      this.saveToProfile();
    }
  }
}
